import { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useFetchETFPriceHistory } from '@etfSecurityHooks';

interface ETFPriceTrendChartProps {
  tickerSymbol: string;
  period: TimeRange;
  compact?: boolean;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatPrice(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function CustomTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const { date, price } = payload[0].payload;
  return (
    <div className="bg-background border border-border rounded-lg shadow-lg p-3">
      <p className="text-xs text-muted-foreground mb-1">
        {new Date(date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })}
      </p>
      <p className="text-sm font-semibold">{formatPrice(price)}</p>
    </div>
  );
}

export function ETFPriceTrendChart({ tickerSymbol, period, compact = false }: ETFPriceTrendChartProps) {
  const { data: priceHistory = [], isLoading } = useFetchETFPriceHistory(tickerSymbol, period);

  const chartData = useMemo(
    () =>
      [...priceHistory]
        .sort((a, b) => new Date(a.fetched_at).getTime() - new Date(b.fetched_at).getTime())
        .map((r) => ({ date: r.fetched_at, price: r.price })),
    [priceHistory]
  );

  const chartHeight = compact ? 60 : 280;

  const chartContent = (
    <>
      {isLoading ? (
        <div
          className="flex items-end justify-between gap-1"
          style={{ height: chartHeight }}
          aria-busy="true"
        >
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="w-full bg-muted animate-pulse rounded-t"
              style={{ height: `${30 + (i % 4) * 15}%` }}
            />
          ))}
        </div>
      ) : chartData.length === 0 ? (
        <div
          className="flex items-center justify-center"
          style={{ height: chartHeight }}
          data-testid="empty-state"
        >
          {!compact && (
            <p className="text-muted-foreground text-sm">
              No price history data available for the selected period.
            </p>
          )}
        </div>
      ) : (
        <div
          role="img"
          aria-label={`${tickerSymbol} price trend chart for ${period} period`}
        >
          <ResponsiveContainer width="100%" height={chartHeight}>
            <LineChart
              data={chartData}
              margin={compact ? { top: 2, right: 2, left: 2, bottom: 2 } : { top: 5, right: 20, left: 10, bottom: 5 }}
            >
              {!compact && <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />}
              {!compact && (
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  className="text-xs"
                  data-testid="x-axis"
                />
              )}
              {!compact && (
                <YAxis
                  tickFormatter={formatPrice}
                  className="text-xs"
                  data-testid="y-axis"
                  domain={['auto', 'auto']}
                />
              )}
              {!compact && <Tooltip content={<CustomTooltip />} />}
              <Line
                type="monotone"
                dataKey="price"
                stroke="hsl(var(--primary))"
                strokeWidth={compact ? 1.5 : 2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  );

  if (compact) {
    return <div data-testid="etf-price-trend-chart-compact">{chartContent}</div>;
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold">
          {tickerSymbol} Price History
        </CardTitle>
      </CardHeader>
      <CardContent>{chartContent}</CardContent>
    </Card>
  );
}
